import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Parse a DMS (Degrees Minutes Seconds) coordinate string to decimal degrees.
 * Supports formats like:
 *   29°54'5.24"N  |  29 54 5.24 N  |  -29.9014
 */
export function parseDMS(dmsStr: string): number {
  const trimmed = dmsStr.trim();
  // Try regex match for DMS format
  const regex = /(\d+)[°\s]+(\d+)['\s]+(\d+\.?\d*)["\s]*([NSEW])?/i;
  const match = regex.exec(trimmed);
  if (match) {
    const [, d, m, s, dir] = match;
    let decimal = parseFloat(d) + parseFloat(m) / 60 + parseFloat(s) / 3600;
    if (dir && (dir.toUpperCase() === 'S' || dir.toUpperCase() === 'W')) {
      decimal = -decimal;
    }
    return decimal;
  }
  // Fall back to plain decimal
  return parseFloat(trimmed);
}

/**
 * Format a coordinate value (decimal degrees) as a DMS string for display.
 */
export function formatDMS(decimal: number, isLat: boolean): string {
  const abs = Math.abs(decimal);
  const d = Math.floor(abs);
  const mFull = (abs - d) * 60;
  const m = Math.floor(mFull);
  const s = ((mFull - m) * 60).toFixed(2);
  const dir = isLat ? (decimal >= 0 ? 'N' : 'S') : (decimal >= 0 ? 'E' : 'W');
  return `${d}°${m}'${s}"${dir}`;
}

/**
 * Format area (in m²) to a human-readable string.
 * < 10 000 m² → "4 523 m²"
 * < 1 000 000 m² → "0.45 ha"
 * ≥ 1 000 000 m² → "1.23 km²"
 */
export function formatArea(sqMeters: number): string {
  if (sqMeters < 10_000) {
    return `${Math.round(sqMeters).toLocaleString('en-IN')} m²`;
  }
  if (sqMeters < 1_000_000) {
    return `${(sqMeters / 10_000).toFixed(2)} ha`;
  }
  return `${(sqMeters / 1_000_000).toFixed(2)} km²`;
}

/**
 * Format distance (in metres) to a human-readable string.
 * < 1 000 m → "452 m"
 * ≥ 1 000 m → "1.45 km"
 */
export function formatDistance(metres: number): string {
  if (metres < 1_000) {
    return `${Math.round(metres)} m`;
  }
  return `${(metres / 1_000).toFixed(2)} km`;
}

/**
 * Returns a relative time label (e.g. "3 min ago", "yesterday").
 */
export function relativeTime(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

/**
 * Safely extract human-readable error messages from Axios / NestJS API responses
 */
export function getApiErrorMessage(error: any, fallbackMessage = 'An unexpected error occurred'): string {
  if (!error) return fallbackMessage;
  if (typeof error === 'string') return error;

  const data = error?.response?.data;
  if (data) {
    if (typeof data.message === 'string' && data.message.trim()) {
      return data.message;
    }
    if (Array.isArray(data.message) && data.message.length > 0) {
      return data.message.map((m: any) => (typeof m === 'string' ? m : JSON.stringify(m))).join('. ');
    }
    if (data.message && typeof data.message === 'object' && data.message.message) {
      return data.message.message;
    }
    if (typeof data.error === 'string' && data.error.trim()) {
      return data.error;
    }
  }

  if (error?.message && typeof error.message === 'string') {
    return error.message;
  }

  return fallbackMessage;
}
